import { prop, sort } from 'f';
import { useContext, useMemo } from 'react';

import { Icon } from 'components/@tedui';
import { Text } from 'components/typography';
import { Language } from 'components/video-player';
import { FormattedMessage } from 'react-intl';

import Group from '../panel/group';
import { SubtitlesContext } from './contexts';
import SubtitlesBodyProps from './subtitlesBody.props';
import SubtitlesItem from './subtitlesItem';

function languageLists(array, isCommon) {
  const [common, uncommon] = array.reduce(
    ([common, uncommon], lang: Language) => {
      return isCommon(lang.languageCode)
        ? [[...common, lang], uncommon]
        : [common, [...uncommon, lang]];
    },
    [[], []]
  );
  if (uncommon.length <= 5) {
    return [common, uncommon, []];
  }
  if (uncommon.length <= 10) {
    return [common, uncommon.splice(0, 5), uncommon];
  }
  const halfIdx = Math.ceil(uncommon.length / 2);
  return [common, uncommon.splice(0, halfIdx), uncommon];
}

const commonLanguageCodes = ['en', 'es', 'fr', 'ja', 'it'];

const SubtitlesBody = ({ languages }: SubtitlesBodyProps) => {
  const { showAllLanguages, setShowAllLanguages, isMobile } =
    useContext(SubtitlesContext);

  const allLanguages = useMemo(() => {
    return sort(
      (a: Language, b: Language) =>
        prop('languageName', a) < prop('languageName', b) ? -1 : 1,
      languages || []
    );
  }, [languages]);

  const validCommon = (langCode: string) =>
    commonLanguageCodes.includes(langCode);

  const [commonLanguages, uncommonLanguages1, uncommonLanguages2] =
    languageLists(allLanguages, validCommon);

  return (
    <div data-testid="subtitles-body">
      {languages.length > 0 ? (
        <div className="flex size-full grow flex-col overflow-y-scroll bg-white p-3 md:h-44 md:flex-row md:overflow-x-visible">
          {/* COMMON LANGUAGES */}
          {commonLanguages.length > 0 && (
            <div className="flex sm:grow md:sticky md:top-0 md:shrink">
              <Group
                className={`flex flex-col border-gray-300 pr-3 ${
                  allLanguages.length !== commonLanguages.length &&
                  'md:border-r-thin'
                } w-full gap-y-3`}
                data-testid="subtitles-common-languages"
              >
                {commonLanguages.map((l: Language) => (
                  <SubtitlesItem
                    language={l}
                    className="row-span-1 sm:last:pb-3 md:last:pb-0"
                    key={l.languageCode}
                  />
                ))}
              </Group>
            </div>
          )}
          {/* SEE MORE LANGUAGES TOGGLE ON MOBILE */}
          {isMobile && uncommonLanguages1.length > 0 && (
            <div>
              <Group border>
                <button
                  type="button"
                  onClick={() => setShowAllLanguages(!showAllLanguages)}
                  className="flex flex-row items-center justify-between whitespace-nowrap py-2 sm:w-full sm:grow"
                >
                  <div data-testid="subtitles-more-languages">
                    <FormattedMessage defaultMessage="More Languages" />
                    <span>
                      &nbsp;(
                      {uncommonLanguages1.length + uncommonLanguages2.length})
                    </span>
                  </div>
                  <div className="h-5 w-5">
                    <Icon
                      iconName={
                        showAllLanguages ? 'chevron-up' : 'chevron-down'
                      }
                      className="flex h-5 items-center justify-center text-2xl text-gray-500"
                    />
                  </div>
                </button>
              </Group>
            </div>
          )}
          {/* UNCOMMON LANGUAGES */}
          {showAllLanguages && uncommonLanguages1.length > 0 && (
            <div
              data-testid="subtitles-uncommon-languages-1"
              className="flex size-full flex-col gap-3 pl-4 sm:h-auto sm:grow sm:pt-1 md:py-1"
            >
              {uncommonLanguages1.map((l: Language) => (
                <SubtitlesItem
                  language={l}
                  key={l.languageCode}
                  className="subtitle-item whitespace-nowrap last:pb-4"
                />
              ))}
            </div>
          )}
          {showAllLanguages && uncommonLanguages2.length > 0 && (
            <div
              data-testid="subtitles-uncommon-languages-2"
              className="flex size-full flex-col gap-3 pl-4 sm:pb-3 sm:pt-1 md:py-1"
            >
              {uncommonLanguages2.map((l: Language) => (
                <SubtitlesItem
                  language={l}
                  key={l.languageCode}
                  className="subtitle-item whitespace-nowrap last:pb-4"
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="flex size-full grow flex-col overflow-y-scroll bg-white p-3 md:h-44 md:flex-row md:overflow-x-visible">
          <div className="flex sm:grow md:sticky md:top-0 md:shrink">
            <Group className="grid w-full grid-flow-col grid-rows-6 flex-col pr-3">
              <Text size="m" className="font-bold">
                No languages available at this time
              </Text>
            </Group>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubtitlesBody;
